import isNumber from 'lodash/isNumber';
import moment from 'moment';
import range from 'lodash/range';
import reduce from 'lodash/reduce';

import { TIME_24H, TIME_AM_PM } from 'src/presentation/components/pages/shared/TimeConstants';

export const getDisabledStartHours = rangeData => {
  // if stop time is set and it's not 12:00 AM,
  // it generates an array of blacklisted hours in future, e.g.
  // stopTime === 19:00 => [19, 20, 21, 22, 23, 23] (max available time to select is 18:59)
  // stopTime === 19:01 => [20, 21, 22, 23, 23] (max available time to select is 19:00)
  const { stopTime, selectedStopHour, selectedStopMinutes } = rangeData;

  if (!stopTime || isTwelveAm(stopTime)) {
    return [];
  }

  return selectedStopMinutes === 0 ? range(selectedStopHour, 24) : range(selectedStopHour + 1, 24);
};

export const getDisabledStartMinutes = rangeData => {
  // generates an array of blacklisted minutes that are later or equal to the start minute
  // if selected stop hour === selected start hour, e.g. selectedStopMinutes === 57 => [57, 58, 59]
  const { stopTime, selectedStartHour, selectedStopHour, selectedStopMinutes } = rangeData;

  if (!stopTime || isTwelveAm(stopTime) || selectedStopHour !== selectedStartHour) {
    return [];
  }

  return range(selectedStopMinutes, 60);
};

export const getDisabledStopHours = rangeData => {
  // generates an array of blacklisted hours that are earlier than the start hour except 12:00 AM, e.g.
  // startTime === 0:xx => [1, ..., 23]
  // startTime === 3:00 => [1, 2]
  // startTime === 2:59 => [1, 2]
  // startTime === 2:58 => [1]
  const { startTime, selectedStartHour, selectedStartMinutes } = rangeData;

  if (!startTime || selectedStartHour === 0) {
    return [];
  }

  return selectedStartMinutes === 59 ? range(1, selectedStartHour + 1) : range(1, selectedStartHour);
};

export const getDisabledStopMinutes = rangeData => {
  // generates an array of blacklisted minutes that are earlier or equal to a start minute
  // if selected stop hour === selected start hour, e.g. selectedStartMinutes === 4 => [0, 1, 2, 3, 4]
  // 12:00 AM (24:00) must always be available as a stop time
  const { startTime, selectedStartHour, selectedStartMinutes, selectedStopHour } = rangeData;

  if (!startTime || !isNumber(selectedStopHour) || (selectedStopHour !== 0 && selectedStartHour !== selectedStopHour)) {
    return [];
  }

  if (selectedStartHour !== selectedStopHour && selectedStopHour === 0) {
    return range(1, 60);
  }

  return selectedStopHour === 0 ? range(1, selectedStartMinutes + 1) : range(0, selectedStartMinutes + 1);
};

export const isTwelveAm = (date: any) => moment(date, [TIME_AM_PM]).format(TIME_AM_PM) === '12:00 AM';

export const splitTime = time =>
  time ? { hours: moment(time, [TIME_24H]).hour(), minutes: moment(time, [TIME_24H]).minutes() } : {};

// converts date to the string in 'm/d/yyyy at hh:mm a' format, e.g. '4/13/2021 at 05:07 PM'
export const toDateTimeString = (date, splitPhrase = ' at '): string => {
  if (!date) {
    return '';
  }
  const dateObj = new Date(date);
  const resultDateString = `${dateObj.toLocaleDateString()}${splitPhrase}${dateObj.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
  if (resultDateString === 'Invalid Date at Invalid Date') {
    return '';
  }
  return resultDateString;
};

export const convertSecondsToMinutesAndSecondsString = (seconds: number) => {
  // Hours, minutes and seconds
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  return reduce(
    [
      [hrs, 'hr'],
      [mins, 'min'],
      [secs, 'sec'],
    ],
    (accumulator: string[], item) => {
      if (item[0] > 0) {
        accumulator.push(`${item[0]} ${item[1]}`);
      }
      return accumulator;
    },
    []
  ).join(' ');
};

export const convertSecondsToMinutes = (seconds): Number => {
  return Math.floor((seconds / 60) * 100) / 100;
};

export const convertMinutesToSeconds = (minutes): Number => {
  return Math.round(minutes * 60);
};
